<script setup lang="ts">
import type { RevalidationReferenceBrandUpdatedDisplayRow, RevalidationSubmitBuildError, RevalidationSubmitBuildErrorField, RevalidationSubmitModalContinuePayload, RevalidationSubmitModalTableRow, SituationAnswer } from '../revalidationsShared.types'
import { SolButton, SolModal } from '@solfacil/girassol'
import { fetchCredentialsCheck } from '~/services-v2/financial-bff/credentials-check.gql'
import { mutateRiskFinancingRegisterCredential } from '~/services-v2/financial-bff/risk-financing-register-credential.gql'
import { mutateRiskFinancingSubmitRevalidations } from '~/services-v2/financial-bff/risk-financing-submit-revalidations.gql'
import { getErrorMessage } from '~/utils/errors'
import { formatBrandDisplay, normalizeBrandKey } from '../revalidations.constants'
import RevalidationSubmitModalStep1 from './RevalidationSubmitModalStep1.vue'
import RevalidationSubmitModalStep2 from './RevalidationSubmitModalStep2.vue'
import RevalidationSubmitModalStep3 from './RevalidationSubmitModalStep3.vue'

const props = withDefaults(
  defineProps<{
    isBatch?: boolean
    rowKey?: string | null
    portalName?: string
    portalUrl?: string | null
    tableRows?: RevalidationSubmitModalTableRow[]
    credentialsCheckBrand?: string
  }>(),
  { isBatch: true, rowKey: null, portalName: 'Portal de Monitoramento', portalUrl: null, tableRows: () => [], credentialsCheckBrand: '' },
)

const emit = defineEmits<{
  continue: [payload?: RevalidationSubmitModalContinuePayload]
  refetch: []
  openHelp: []
}>()

const open = defineModel<boolean>('open', { default: false })

const modalStep = ref<1 | 2 | 3>(1)

const installationFullyComplete = ref<SituationAnswer>('no')
const monitoringPlantRegistered = ref<SituationAnswer>('no')
const recentEnergyGeneration = ref<SituationAnswer>('no')
const acceptanceConfirmed = ref(false)

const installerLogin = ref('')
const installerPassword = ref('')
const portalConnectionAuthorized = ref(false)

const credentialsCheckPending = ref(false)
const hasCredentials = ref<boolean | null>(null)
const platformAccessToken = ref<string | null>(null)
const registerCredentialPending = ref(false)
const registerCredentialErrorMessage = ref<string | null>(null)
const submitRevalidationsPending = ref(false)
const submitRevalidationsErrorMessage = ref<string | null>(null)
const submitRevalidationsFailed = ref(false)
const credentialRegistered = ref(false)
const step1ValidationError = ref(false)
const referenceBrandUpdatedRows = ref<RevalidationReferenceBrandUpdatedDisplayRow[]>([])
const submitAwaitingReferenceBrandAck = ref(false)

watch(open, (isOpen) => {
  if (isOpen) {
    modalStep.value = 1
    credentialsCheckPending.value = false
    hasCredentials.value = null
    platformAccessToken.value = null
    registerCredentialPending.value = false
    registerCredentialErrorMessage.value = null
    submitRevalidationsPending.value = false
    submitRevalidationsErrorMessage.value = null
    submitRevalidationsFailed.value = false
    credentialRegistered.value = false
    step1ValidationError.value = false
    referenceBrandUpdatedRows.value = []
    submitAwaitingReferenceBrandAck.value = false
    installationFullyComplete.value = 'no'
    monitoringPlantRegistered.value = 'no'
    recentEnergyGeneration.value = 'no'
    acceptanceConfirmed.value = false
    installerLogin.value = ''
    installerPassword.value = ''
    portalConnectionAuthorized.value = false
  }
})

function close() {
  if (submitAwaitingReferenceBrandAck.value)
    finishAfterReferenceBrandWarning()

  open.value = false
}

function finishAfterReferenceBrandWarning() {
  submitAwaitingReferenceBrandAck.value = false
  referenceBrandUpdatedRows.value = []
  emit('continue', { referenceBrandUpdated: true })
}

function onAcknowledgeReferenceBrandWarning() {
  finishAfterReferenceBrandWarning()
  open.value = false
}

const submitContextAttr = computed(() => `${props.isBatch ? 'batch' : 'single'}:${props.rowKey ?? ''}`)

const isLoading = computed(
  () => credentialsCheckPending.value || registerCredentialPending.value || submitRevalidationsPending.value,
)

const portalTitle = computed(() => `Conecte-se com o portal ${props.portalName}`)

const modalTitle = computed(() => {
  if (modalStep.value === 3)
    return 'Marca fora da campanha'
  if (submitRevalidationsFailed.value)
    return portalTitle.value
  if (isLoading.value || modalStep.value === 2)
    return portalTitle.value
  return 'Confira a instalação a ser revalidada'
})

const monitoringPlantQuestion = computed(
  () => `A planta de monitoramento já foi cadastrada no ${props.portalName}?`,
)

const portalExternalHref = computed((): string | null => {
  const raw = props.portalUrl?.trim() ?? ''
  if (!raw || raw === '#')
    return null
  return /^https?:\/\//i.test(raw) ? raw : null
})

const acceptanceLabel = computed(() =>
  props.isBatch
    ? 'Confirmo que os números de série informados pertencem às instalações selecionadas e que a veracidade dessas informações é de minha responsabilidade.'
    : 'Confirmo que os números de série informados pertencem à instalação selecionada e que a veracidade dessas informações é de minha responsabilidade.',
)

const canContinue = computed(
  () =>
    acceptanceConfirmed.value
    && installationFullyComplete.value === 'yes'
    && monitoringPlantRegistered.value === 'yes'
    && recentEnergyGeneration.value === 'yes',
)

const bannerInverterBrandLabel = computed(() => {
  const label = props.tableRows?.[0]?.inverterBrandLabel?.trim()
  if (!label || label === '—')
    return 'este inversor'
  return label
})

const canConnectAndRevalidate = computed(
  () =>
    portalConnectionAuthorized.value
    && installerLogin.value.trim().length > 0
    && installerPassword.value.length > 0,
)

interface SubmitRevalidationsVariables {
  input: {
    platform_access_token: string
    submissions: Array<{
      revalidation_id: string
      monitoring_plant_name: string
      devices: Array<{ brand: string, serial_number: string }>
    }>
  }
}

function buildSubmitInputFromTableRows(accessToken: string): {
  variables: SubmitRevalidationsVariables | null
  errors: RevalidationSubmitBuildError[]
} {
  if (props.tableRows.length === 0)
    return { variables: null, errors: [] }

  const submissions: SubmitRevalidationsVariables['input']['submissions'] = []
  const errors: RevalidationSubmitBuildError[] = []

  for (const row of props.tableRows) {
    const rid = row.revalidationId.trim()
    const plant = row.plantName.trim()
    const brandSlug = row.deviceBrandSlug.trim()
    const devices = row.serials
      .map(s => s.trim())
      .filter(Boolean)
      .map(serial_number => ({ brand: brandSlug, serial_number }))

    let rowInvalid = false
    if (!rid) {
      errors.push({ rowKey: row.rowKey, field: 'revalidationId' })
      rowInvalid = true
    }
    if (!plant) {
      errors.push({ rowKey: row.rowKey, field: 'plantName' })
      rowInvalid = true
    }
    if (!brandSlug) {
      errors.push({ rowKey: row.rowKey, field: 'brand' })
      rowInvalid = true
    }
    if (devices.length === 0) {
      errors.push({ rowKey: row.rowKey, field: 'serials' })
      rowInvalid = true
    }
    if (rowInvalid)
      continue

    submissions.push({
      revalidation_id: rid,
      monitoring_plant_name: plant,
      devices,
    })
  }

  return { variables: { input: { platform_access_token: accessToken, submissions } }, errors }
}

function buildReferenceBrandUpdatedRows(
  items: Array<{ revalidation_id: string, reference_inverter_brand: string }>,
): RevalidationReferenceBrandUpdatedDisplayRow[] {
  return items.map((item) => {
    const row = props.tableRows.find(
      tableRow => tableRow.revalidationId.trim() === item.revalidation_id.trim(),
    )
    return {
      revalidationId: item.revalidation_id,
      projectName: row?.projectName?.trim() ? row.projectName.trim() : '—',
      brandLabel: formatBrandDisplay(item.reference_inverter_brand),
    }
  })
}

function handleSuccessfulSubmit(
  referenceBrandUpdated: Array<{ revalidation_id: string, reference_inverter_brand: string }>,
): boolean {
  if (referenceBrandUpdated.length > 0) {
    referenceBrandUpdatedRows.value = buildReferenceBrandUpdatedRows(referenceBrandUpdated)
    submitAwaitingReferenceBrandAck.value = true
    modalStep.value = 3
    return true
  }

  emit('continue', { referenceBrandUpdated: false })
  return true
}

async function submitRevalidationsAndFinish(accessToken: string): Promise<boolean> {
  const { variables, errors } = buildSubmitInputFromTableRows(accessToken)

  if (errors.length > 0) {
    const fieldLabels: Record<RevalidationSubmitBuildErrorField, string> = {
      serials: 'números de série',
      plantName: 'nome da planta',
      brand: 'marca do inversor',
      revalidationId: 'ID da revalidação',
    }
    const lines = new Set<string>()
    for (const e of errors) {
      const row = props.tableRows.find(r => r.rowKey === e.rowKey)
      const name = (row?.projectName?.trim()) ? row.projectName.trim() : '—'
      lines.add(`«${name}»: ${fieldLabels[e.field] ?? e.field}`)
    }
    submitRevalidationsErrorMessage.value = `Dados incompletos:\n${[...lines].join('\n')}`
    return false
  }

  if (!variables || variables.input.submissions.length === 0) {
    submitRevalidationsErrorMessage.value
      = 'Não foi possível enviar: verifique os números de série, a planta e a marca em cada linha selecionada.'
    return false
  }

  submitRevalidationsErrorMessage.value = null
  submitRevalidationsFailed.value = false
  submitRevalidationsPending.value = true
  try {
    const result = await mutateRiskFinancingSubmitRevalidations(variables)
    if (result.success)
      return handleSuccessfulSubmit(result.data.reference_brand_updated ?? [])
    submitRevalidationsFailed.value = true
    return false
  }
  catch {
    submitRevalidationsFailed.value = true
    return false
  }
  finally {
    submitRevalidationsPending.value = false
    // Always refetch so the parent keeps list and totals in sync,
    // regardless of whether the submit succeeded or failed.
    emit('refetch')
  }
}

async function onContinueFromStep1() {
  if (credentialsCheckPending.value || submitRevalidationsPending.value)
    return
  if (!canContinue.value) {
    step1ValidationError.value = true
    return
  }
  step1ValidationError.value = false

  const brand = normalizeBrandKey(props.credentialsCheckBrand).replace(/-/g, '_')
  if (!brand) {
    modalStep.value = 2
    return
  }

  credentialsCheckPending.value = true
  try {
    const result = await fetchCredentialsCheck({ brand })
    hasCredentials.value = result.has_credentials ?? null
    platformAccessToken.value = result.access_token ?? null
    if (result.is_valid && result.access_token)
      await submitRevalidationsAndFinish(result.access_token)
    else
      modalStep.value = 2
  }
  catch {
    modalStep.value = 2
  }
  finally {
    credentialsCheckPending.value = false
  }
}

function formatRegisterCredentialMessage(code: string | null | undefined): string {
  if (!code)
    return 'Não foi possível registar as credenciais. Tente novamente.'

  const map: Record<string, string> = {
    VALIDATION_ERROR: 'Os dados informados não são válidos. Verifique e tente novamente.',
    CREDENTIAL_MISMATCH: 'Usuário ou senha incorretos para este portal.',
    CREDENTIAL_ALREADY_EXISTS: 'Estas credenciais já estão registadas.',
    END_CUSTOMER_CREDENTIAL: 'Não é permitido utilizar credencial de cliente final.',
    TECHNICAL_FAILURE: 'Erro técnico ao validar as credenciais. Tente novamente mais tarde.',
  }

  return map[code] ?? `Não foi possível conectar (${code}).`
}

async function onConnectAndRevalidate() {
  if (!canConnectAndRevalidate.value || registerCredentialPending.value || submitRevalidationsPending.value)
    return

  const brand = normalizeBrandKey(props.credentialsCheckBrand).replace(/-/g, '_')
  if (!brand) {
    registerCredentialErrorMessage.value = 'Marca do inversor indisponível. Tente novamente.'
    return
  }

  registerCredentialErrorMessage.value = null
  submitRevalidationsErrorMessage.value = null

  if (!credentialRegistered.value) {
    registerCredentialPending.value = true
    try {
      const result = await mutateRiskFinancingRegisterCredential({
        input: {
          brand,
          username: installerLogin.value.trim(),
          password: installerPassword.value,
        },
      })
      if (!result.success) {
        registerCredentialErrorMessage.value = formatRegisterCredentialMessage(result.message ?? undefined)
        return
      }
      credentialRegistered.value = true
    }
    catch (err) {
      registerCredentialErrorMessage.value = getErrorMessage(err)
      return
    }
    finally {
      registerCredentialPending.value = false
    }
  }

  credentialsCheckPending.value = true
  let accessToken: string | null = null
  try {
    const checkResult = await fetchCredentialsCheck({ brand })
    hasCredentials.value = checkResult.has_credentials ?? null
    platformAccessToken.value = checkResult.access_token ?? null
    accessToken = checkResult.access_token ?? null
  }
  catch {
    registerCredentialErrorMessage.value = 'Não foi possível obter o token de acesso. Tente novamente.'
    return
  }
  finally {
    credentialsCheckPending.value = false
  }

  if (!accessToken) {
    registerCredentialErrorMessage.value = 'Não foi possível obter o token de acesso. Tente novamente.'
    return
  }

  await submitRevalidationsAndFinish(accessToken)
}

function handleSecondaryAction() {
  if (credentialsCheckPending.value || registerCredentialPending.value || submitRevalidationsPending.value)
    return
  if (modalStep.value === 3) {
    finishAfterReferenceBrandWarning()
    open.value = false
    return
  }
  if (modalStep.value === 2) {
    registerCredentialErrorMessage.value = null
    submitRevalidationsErrorMessage.value = null
    credentialRegistered.value = false
    modalStep.value = 1
  }
  else {
    close()
  }
}

async function retrySubmit() {
  submitRevalidationsFailed.value = false
  if (modalStep.value === 2)
    await onConnectAndRevalidate()
  else if (platformAccessToken.value)
    await submitRevalidationsAndFinish(platformAccessToken.value)
}
</script>

<template>
  <SolModal
    id="revalidation-submit-modal"
    :is-open="open"
    :title="modalTitle"
    :size="{ desktop: 'extra-large', mobile: 'bottom-sheet' }"
    @close="close"
  >
    <div v-if="isLoading" class="revalidation-submit-modal-loading">
      <div class="revalidation-submit-modal-spinner" />
      <span class="revalidation-submit-modal-loading-text">Verificando informações</span>
    </div>

    <div v-else-if="submitRevalidationsFailed" class="revalidation-submit-modal-error">
      <img
        src="/images/connection-error.svg"
        alt=""
        class="revalidation-submit-modal-error__img"
        aria-hidden="true"
      >
      <p class="revalidation-submit-modal-error__title">
        Não foi possível conectar ao portal de monitoramento
      </p>
      <p class="revalidation-submit-modal-error__description">
        Houve alguma instabilidade na conexão com o {{ portalName }}. Por favor, tente novamente dentro de alguns instantes.
      </p>
    </div>

    <template v-else>
      <RevalidationSubmitModalStep1
        v-if="modalStep === 1"
        v-model:installation-fully-complete="installationFullyComplete"
        v-model:monitoring-plant-registered="monitoringPlantRegistered"
        v-model:recent-energy-generation="recentEnergyGeneration"
        v-model:acceptance-confirmed="acceptanceConfirmed"
        :submit-context-attr="submitContextAttr"
        :monitoring-plant-question="monitoringPlantQuestion"
        :acceptance-label="acceptanceLabel"
        :table-rows="tableRows"
        :show-validation-error="step1ValidationError"
      />
      <RevalidationSubmitModalStep2
        v-else-if="modalStep === 2"
        v-model:installer-login="installerLogin"
        v-model:installer-password="installerPassword"
        v-model:portal-connection-authorized="portalConnectionAuthorized"
        :submit-context-attr="submitContextAttr"
        :portal-name="portalName"
        :portal-external-href="portalExternalHref"
        :banner-inverter-brand-label="bannerInverterBrandLabel"
        :register-credential-error-message="registerCredentialErrorMessage"
        :submit-revalidations-error-message="submitRevalidationsErrorMessage"
        @open-help="emit('openHelp')"
      />
      <RevalidationSubmitModalStep3
        v-else
        :submit-context-attr="submitContextAttr"
        :rows="referenceBrandUpdatedRows"
        :is-batch="isBatch"
      />
    </template>

    <template #action-secondary>
      <SolButton
        v-if="!isLoading && modalStep !== 3"
        id="revalidation-submit-back"
        variant="secondary"
        size="medium"
        @click="submitRevalidationsFailed ? close() : handleSecondaryAction()"
      >
        {{ submitRevalidationsFailed ? 'Cancelar' : 'Voltar' }}
      </SolButton>
    </template>

    <template #action-primary>
      <SolButton
        v-if="submitRevalidationsFailed"
        id="revalidation-submit-retry"
        variant="primary"
        size="medium"
        :disabled="isLoading"
        @click="retrySubmit"
      >
        Tentar novamente
      </SolButton>
      <SolButton
        v-else-if="modalStep === 3"
        id="revalidation-submit-ack-reference-brand"
        variant="primary"
        size="medium"
        :disabled="isLoading"
        @click="onAcknowledgeReferenceBrandWarning"
      >
        Entendi
      </SolButton>
      <SolButton
        v-else-if="modalStep === 1"
        id="revalidation-submit-continue"
        variant="primary"
        size="medium"
        :disabled="isLoading"
        @click="onContinueFromStep1"
      >
        Continuar
      </SolButton>
      <SolButton
        v-else
        id="revalidation-submit-connect"
        variant="primary"
        size="medium"
        :disabled="isLoading || !canConnectAndRevalidate"
        @click="onConnectAndRevalidate"
      >
        Conectar e revalidar
      </SolButton>
    </template>
  </SolModal>
</template>

<style scoped lang="scss">
.revalidation-submit-modal-loading {
  @apply flex flex-col justify-center items-center w-full gap-2;
  min-height: 40vh;
}

.revalidation-submit-modal-spinner {
  position: relative;
  background-image: url('/images/installation/spinner-inner.png');
  background-size: 50%;
  background-position: center;
  background-repeat: no-repeat;
  width: 100px;
  height: 100px;

  &::after {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    top: 3px;
    left: 0;
    background-image: url('/images/installation/spinner-outer.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    animation: revalidation-spin 1s linear infinite;
    transform-origin: center;
  }
}

.revalidation-submit-modal-loading-text {
  @apply text-xs text-neutral-low-light mt-2xs;
}

.revalidation-submit-modal-error {
  @apply flex flex-col justify-center items-center w-full text-center;
  gap: 12px;
  min-height: 40vh;

  &__img {
    width: 180px;
    height: auto;
    margin-bottom: 4px;
  }

  &__title {
    @apply fonts-body-large-bold text-neutral-low-dark leading-[1.375];
    margin: 0;
  }

  &__description {
    @apply fonts-body-medium-regular leading-[1.5];
    margin: 0;
    max-width: 380px;
  }
}
</style>

<style>
@keyframes revalidation-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
